import { toast } from 'sonner';

/**
 * Service de notification pour afficher des messages à l'utilisateur
 * Utilise la bibliothèque sonner pour afficher des toasts
 */
export class NotificationService {
  /**
   * Affiche un message d'erreur d'authentification avec des détails utiles
   * @param title Titre du message
   * @param error Erreur à afficher
   * @param suggestions Suggestions pour résoudre le problème
   */
  static showAuthError(title: string, error: any, suggestions?: string[]): void {
    console.error('🔐 Erreur d\'authentification:', error);
    
    // Extraire le message d'erreur
    let errorMessage = '';
    if (typeof error === 'string') {
      errorMessage = error;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else if (error?.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.response?.statusText) {
      errorMessage = `${error.response.status}: ${error.response.statusText}`;
    } else if (error?.message) {
      errorMessage = error.message;
    } else {
      errorMessage = 'Une erreur inconnue est survenue';
    }
    
    // Préparer le contenu des suggestions
    let suggestionsContent = '';
    if (suggestions && suggestions.length > 0) {
      suggestionsContent = '\n\nSuggestions :\n' + 
        suggestions.map(suggestion => `• ${suggestion}`).join('\n');
    }
    
    // Afficher le toast
    toast.error(`${errorMessage}${suggestionsContent}`, {
      id: `auth-error-${Date.now()}`,
      duration: 8000, // Afficher plus longtemps pour que l'utilisateur puisse lire
      className: 'auth-error-toast',
      description: title || 'Problème d\'authentification'
    });
  }
  
  /**
   * Affiche un message de succès d'authentification
   * @param title Titre du message
   * @param message Message à afficher
   */
  static showAuthSuccess(title: string, message: string): void {
    toast.success(message, {
      id: `auth-success-${Date.now()}`,
      duration: 3000,
      className: 'auth-success-toast',
      description: title
    });
  }
  
  /**
   * Affiche un message d'information sur l'authentification
   * @param title Titre du message
   * @param message Message à afficher
   */
  static showAuthInfo(title: string, message: string): void {
    toast.info(message, {
      id: `auth-info-${Date.now()}`,
      duration: 5000,
      className: 'auth-info-toast',
      description: title
    });
  }
  
  /**
   * Affiche un message d'avertissement sur l'authentification
   * @param title Titre du message
   * @param message Message à afficher
   * @param suggestions Suggestions pour résoudre le problème
   */
  static showAuthWarning(title: string, message: string, suggestions?: string[]): void {
    // Préparer le contenu des suggestions
    let suggestionsContent = '';
    if (suggestions && suggestions.length > 0) {
      suggestionsContent = '\n\nSuggestions :\n' + 
        suggestions.map(suggestion => `• ${suggestion}`).join('\n');
    }
    
    // Afficher le toast
    toast.warning(`${message}${suggestionsContent}`, {
      id: `auth-warning-${Date.now()}`,
      duration: 6000,
      className: 'auth-warning-toast',
      description: title
    });
  }
  
  /**
   * Affiche un message de session expirée
   */
  static showSessionExpired(): void {
    const suggestions = [
      'Reconnectez-vous pour continuer',
      'Vérifiez votre connexion internet',
      'Si le problème persiste, contactez l\'administrateur'
    ];
    
    this.showAuthWarning(
      'Session expirée',
      'Votre session a expiré en raison d\'une période d\'inactivité',
      suggestions
    );
  }
  
  /**
   * Affiche un message d'erreur de token
   * @param error Erreur à afficher
   */
  static showTokenError(error: any): void {
    const suggestions = [
      'Essayez de vous reconnecter',
      'Effacez les cookies de votre navigateur',
      'Vérifiez que vous n\'êtes pas connecté sur un autre appareil',
      'Si le problème persiste, contactez l\'administrateur'
    ];
    
    this.showAuthError(
      'Problème d\'authentification',
      error,
      suggestions
    );
  }
  
  /**
   * Affiche un message de rafraîchissement de token réussi
   */
  static showTokenRefreshed(): void {
    this.showAuthInfo(
      'Authentification',
      'Votre session a été prolongée automatiquement'
    );
  }
  
  /**
   * Affiche un message de succès générique
   * @param title Titre du message
   * @param message Message à afficher
   */
  static showSuccess(title: string, message: string): void {
    toast.success(message, {
      id: `success-${Date.now()}`,
      duration: 4000,
      className: 'success-toast',
      description: title
    });
  }
  
  /**
   * Affiche un message d'erreur générique
   * @param title Titre du message
   * @param message Message à afficher
   */
  static showError(title: string, message: string): void {
    toast.error(message, {
      id: `error-${Date.now()}`,
      duration: 6000,
      className: 'error-toast',
      description: title
    });
  }
  
  /**
   * Affiche un message d'information générique
   * @param title Titre du message
   * @param message Message à afficher
   */
  static showInfo(title: string, message: string): void {
    toast.info(message, {
      id: `info-${Date.now()}`,
      duration: 5000,
      className: 'info-toast',
      description: title
    });
  }
  
  /**
   * Affiche un message d'avertissement générique
   * @param title Titre du message
   * @param message Message à afficher
   */
  static showWarning(title: string, message: string): void {
    toast.warning(message, {
      id: `warning-${Date.now()}`,
      duration: 5000,
      className: 'warning-toast',
      description: title
    });
  }
}
